import { useCallback, useEffect, useState } from "react";
import { ChevronRight, Plus } from "lucide-react";
import { api, type Absence, type Account } from "../api";
import { Select } from "./Select";
import { bestaetigen, datumDe, fehlerMelden, fehlerText, toast } from "./verwaltung";

const TAGE = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"];
/** Anzeige in deutscher Wochenordnung: Montag zuerst. */
const TAGE_REIHE = [1, 2, 3, 4, 5, 6, 0];

const MODI = [
  {
    id: "still" as const,
    name: "Still",
    marke: "nichts geht raus",
    klasse: "eu",
    note: "Sortiert weiter, schreibt aber keine Entwürfe und meldet sich nicht. Du kommst in ein aufgeräumtes Postfach zurück.",
  },
  {
    id: "vorbereitend" as const,
    name: "Vorbereitend",
    marke: "nichts geht raus",
    klasse: "eu",
    note: "Schreibt Entwürfe, die deine Abwesenheit beiläufig erwähnen. Verschickt wird erst auf deinen Klick.",
  },
  {
    id: "antwortend" as const,
    name: "Antwortend",
    marke: "verschickt Mails",
    klasse: "av-vertrag",
    note: "Verschickt eine echte Abwesenheitsnotiz — einmal je Absender, nie an Newsletter, Verteiler, Spam oder automatische Absender.",
  },
];

const leer = (accountId: number): Absence => ({
  id: 0,
  accountId,
  name: "",
  kind: "zeitraum",
  startsOn: new Date().toISOString().slice(0, 10),
  endsOn: null,
  weekdays: [1, 2, 3, 4, 5],
  startTime: "18:00",
  endTime: "08:00",
  subject: "Abwesenheitsnotiz",
  body: "Vielen Dank für Ihre Nachricht.\n\nIch bin derzeit nicht im Büro und lese meine Mails erst nach meiner Rückkehr. Ihre Nachricht geht nicht verloren — ich melde mich, sobald ich zurück bin.\n\nIn dringenden Fällen wenden Sie sich bitte an …\n\nViele Grüße",
  mode: "vorbereitend",
  active: true,
});

export function AbwesenheitPanel({ accounts }: { accounts: Account[] }) {
  const [liste, setListe] = useState<Absence[] | null>(null);
  const [ladeFehler, setLadeFehler] = useState<string | null>(null);
  const [entwurf, setEntwurf] = useState<Absence | null>(null);
  const [busy, setBusy] = useState(false);
  const [versucht, setVersucht] = useState(false);

  const laden = useCallback(async () => {
    try {
      setListe(await api.absences());
      setLadeFehler(null);
    } catch (e) {
      setLadeFehler(fehlerText(e));
    }
  }, []);
  useEffect(() => { void laden(); }, [laden]);

  const oeffnen = (a: Absence | null) => { setEntwurf(a); setVersucht(false); };

  const speichern = async () => {
    if (!entwurf) return;
    setVersucht(true);
    if (pruefen(entwurf).length) return;
    setBusy(true);
    try {
      if (entwurf.id) await api.updateAbsence(entwurf.id, entwurf);
      else await api.createAbsence(entwurf);
      toast.erfolg(entwurf.id ? `„${entwurf.name}" gespeichert.` : `„${entwurf.name}" angelegt.`);
      oeffnen(null);
      await laden();
    } catch (e) {
      fehlerMelden(e, "Nicht gespeichert");
    } finally {
      setBusy(false);
    }
  };

  const loeschen = async (a: Absence) => {
    const ja = await bestaetigen({
      titel: `Abwesenheit „${a.name}" löschen?`,
      text: a.aktivJetzt ? "Sie läuft gerade — danach verhält sich der Agent sofort wieder normal." : undefined,
      bestaetigen: "Löschen",
      gefahr: true,
    });
    if (!ja) return;
    try {
      await api.deleteAbsence(a.id);
      toast.erfolg(`„${a.name}" gelöscht.`);
      oeffnen(null);
      await laden();
    } catch (e) {
      fehlerMelden(e, "Löschen fehlgeschlagen");
    }
  };

  const fehler = entwurf && versucht ? pruefen(entwurf) : [];

  return (
    <>
      <div className="v-kopf">
        <div>
          <h2>Abwesenheit</h2>
          <p className="lede">
            Für Urlaub als Zeitraum, für Feierabend und Wochenende als wiederkehrende Zeit. Greifen mehrere
            gleichzeitig, gilt die erste in der Liste.
          </p>
        </div>
        <div className="v-kopf-aktionen">
          <button className="btn primary" disabled={accounts.length === 0 || entwurf?.id === 0}
            title={accounts.length === 0 ? "Erst ein Postfach verbinden" : undefined}
            onClick={() => oeffnen(leer(accounts[0]?.id ?? 0))}>
            <Plus aria-hidden="true" /> Abwesenheit hinzufügen
          </button>
        </div>
      </div>

      {entwurf?.id === 0 && (
        <div className="v-panel">
          <div className="v-panel-rumpf">
            <h3 style={{ margin: "0 0 8px", fontSize: 15 }}>Neue Abwesenheit</h3>
            <Formular wert={entwurf} setzen={setEntwurf} accounts={accounts} />
            <FehlerListe fehler={fehler} />
            <div className="v-knoepfe" style={{ marginTop: 14 }}>
              <button className="btn primary" disabled={busy} onClick={() => void speichern()}>{busy ? "Speichert …" : "Anlegen"}</button>
              <button className="btn quiet" onClick={() => oeffnen(null)}>Abbrechen</button>
            </div>
          </div>
        </div>
      )}

      {ladeFehler && !liste ? (
        <div className="v-leer" data-art="fehler">
          <strong>Die Abwesenheiten ließen sich nicht laden</strong>
          <span>{ladeFehler}</span>
          <button className="btn" onClick={() => void laden()}>Erneut versuchen</button>
        </div>
      ) : !liste ? (
        <div className="v-laden" role="status">Lade …</div>
      ) : liste.length === 0 && entwurf?.id !== 0 ? (
        <div className="v-leer">
          <strong>Keine Abwesenheit eingetragen</strong>
          <span>Der Agent arbeitet wie gewohnt. Für Urlaub oder feste Bürozeiten oben eine anlegen.</span>
        </div>
      ) : (
        liste.map((a) => {
          const offen = entwurf?.id === a.id;
          return (
            <div className="rule" key={a.id} data-disabled={!a.active}>
              <div
                className="rule-head"
                role="button"
                tabIndex={0}
                aria-expanded={offen}
                onClick={() => oeffnen(offen ? null : { ...a })}
                onKeyDown={(e) => { if (e.target === e.currentTarget && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); oeffnen(offen ? null : { ...a }); } }}
              >
                <ChevronRight className="v-aufklapp" data-offen={offen} aria-hidden="true" />
                <span className="state-dot" data-state={a.aktivJetzt ? "da" : a.active ? "fehlt" : "nicht_erwartet"} aria-hidden="true" />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="rule-name">{a.name}</div>
                  <div className="rule-summary">
                    {beschreibung(a)}
                    {accounts.length > 1 && ` · ${accounts.find((x) => x.id === a.accountId)?.email ?? ""}`}
                  </div>
                </div>
                {a.aktivJetzt && <span className="chip" style={{ ["--chip-color" as string]: "var(--ok)" }}>läuft gerade</span>}
                {a.mode === "antwortend" && (a.antworten ?? 0) > 0 && (
                  <span className="chip v-schmal-weg">{a.antworten} beantwortet</span>
                )}
                {!a.active && <span className="v-marke">aus</span>}
              </div>

              {offen && entwurf && (
                <div className="rule-body">
                  <Formular wert={entwurf} setzen={setEntwurf} accounts={accounts} />
                  <FehlerListe fehler={fehler} />
                  <div className="v-knoepfe" style={{ marginTop: 14 }}>
                    <button className="btn primary" disabled={busy} onClick={() => void speichern()}>{busy ? "Speichert …" : "Speichern"}</button>
                    <button className="btn quiet" onClick={() => oeffnen(null)}>Abbrechen</button>
                    <div className="spacer" />
                    <button className="btn danger" onClick={() => void loeschen(a)}>Löschen</button>
                  </div>
                </div>
              )}
            </div>
          );
        })
      )}
    </>
  );
}

function FehlerListe({ fehler }: { fehler: string[] }) {
  if (fehler.length === 0) return null;
  return <div className="v-meldung" data-art="fehler" role="alert" style={{ marginTop: 12 }}><span>{fehler.join(" ")}</span></div>;
}

function pruefen(a: Absence): string[] {
  const f: string[] = [];
  if (!a.name.trim()) f.push("Bitte eine Bezeichnung eingeben.");
  if (!a.accountId) f.push("Bitte ein Postfach wählen.");
  if (a.kind === "zeitraum") {
    if (!a.startsOn) f.push("Bitte ein Startdatum wählen.");
    if (a.startsOn && a.endsOn && a.endsOn < a.startsOn) f.push("„Bis“ liegt vor „Von“.");
  } else {
    if (a.weekdays.length === 0) f.push("Bitte mindestens einen Wochentag wählen.");
    if (Boolean(a.startTime) !== Boolean(a.endTime)) f.push("Bitte beide Uhrzeiten angeben — oder beide leer lassen für ganztägig.");
  }
  if (a.mode === "antwortend" && (!a.subject.trim() || !a.body.trim())) f.push("Für automatische Antworten braucht es Betreff und Text.");
  return f;
}

function beschreibung(a: Absence): string {
  const modus = MODI.find((m) => m.id === a.mode)?.name ?? a.mode;
  if (a.kind === "zeitraum") {
    const von = a.startsOn ? datumDe(a.startsOn) : "…";
    const bis = a.endsOn ? datumDe(a.endsOn) : "offen";
    return `${von} bis ${bis} · ${modus}`;
  }
  const tage = a.weekdays.length === 7 || a.weekdays.length === 0
    ? "täglich"
    : TAGE_REIHE.filter((d) => a.weekdays.includes(d)).map((d) => TAGE[d]).join(", ");
  const zeit = a.startTime && a.endTime ? ` ${a.startTime}–${a.endTime} Uhr` : " ganztägig";
  return `${tage}${zeit} · ${modus}`;
}

function Formular({
  wert, setzen, accounts,
}: {
  wert: Absence;
  setzen: (a: Absence) => void;
  accounts: Account[];
}) {
  const set = (patch: Partial<Absence>) => setzen({ ...wert, ...patch });

  return (
    <div style={{ paddingTop: 8 }}>
      <div className="v-raster">
        <div className="field">
          <label htmlFor="ab-name">Bezeichnung</label>
          <input id="ab-name" className="input" autoFocus={!wert.id} value={wert.name} onChange={(e) => set({ name: e.target.value })} placeholder="z. B. Sommerurlaub" />
        </div>
        <div className="field">
          <label>Postfach</label>
          <Select
            value={String(wert.accountId)}
            onChange={(v) => set({ accountId: Number(v) })}
            options={accounts.map((a) => ({ value: String(a.id), label: a.email }))}
            placeholder="Postfach wählen …"
            aria-label="Postfach"
          />
        </div>
      </div>

      <div className="field">
        <label id="ab-art">Art</label>
        <div className="tabs" role="group" aria-labelledby="ab-art" style={{ alignSelf: "flex-start" }}>
          <button
            type="button"
            className="tab"
            aria-pressed={wert.kind === "zeitraum"}
            onClick={() => set({ kind: "zeitraum" })}
          >
            Zeitraum
          </button>
          <button
            type="button"
            className="tab"
            aria-pressed={wert.kind === "wiederkehrend"}
            onClick={() => set({ kind: "wiederkehrend" })}
          >
            Wiederkehrend
          </button>
        </div>
      </div>

      {wert.kind === "zeitraum" ? (
        <div className="v-raster">
          <div className="field">
            <label htmlFor="ab-von">Von</label>
            <input id="ab-von" className="input" type="date" value={wert.startsOn ?? ""} onChange={(e) => set({ startsOn: e.target.value || null })} />
          </div>
          <div className="field">
            <label htmlFor="ab-bis">Bis <span className="v-optional">(leer = offen)</span></label>
            <input id="ab-bis" className="input" type="date" min={wert.startsOn ?? undefined} value={wert.endsOn ?? ""} onChange={(e) => set({ endsOn: e.target.value || null })} />
          </div>
        </div>
      ) : (
        <>
          <div className="field">
            <label id="ab-tage">An diesen Tagen</label>
            <div className="tage-wahl" role="group" aria-labelledby="ab-tage">
              {TAGE_REIHE.map((i) => { const t = TAGE[i]!; return (
                <button
                  key={t}
                  type="button"
                  className="chip pick"
                  aria-pressed={wert.weekdays.includes(i)}
                  data-on={wert.weekdays.includes(i)}
                  onClick={() =>
                    set({
                      weekdays: wert.weekdays.includes(i)
                        ? wert.weekdays.filter((d) => d !== i)
                        : [...wert.weekdays, i].sort(),
                    })
                  }
                >
                  {t}
                </button>
              ); })}
            </div>
          </div>
          <div className="v-raster">
            <div className="field">
              <label htmlFor="ab-zvon">Von (Uhrzeit)</label>
              <input id="ab-zvon" className="input" type="time" value={wert.startTime ?? ""} onChange={(e) => set({ startTime: e.target.value || null })} />
            </div>
            <div className="field">
              <label htmlFor="ab-zbis">Bis (Uhrzeit)</label>
              <input id="ab-zbis" className="input" type="time" value={wert.endTime ?? ""} onChange={(e) => set({ endTime: e.target.value || null })} />
            </div>
          </div>
          <p className="hint" style={{ marginTop: -6, marginBottom: 14 }}>
            Ist „Bis" früher als „Von", läuft das Fenster über Mitternacht — etwa 18:00 bis 08:00 für
            den Feierabend. Beide leer heißt ganztägig.
          </p>
        </>
      )}

      <div className="field">
        <label id="ab-modus">Was der Agent in der Zeit tun soll</label>
        <div className="provider-list" role="radiogroup" aria-labelledby="ab-modus">
          {MODI.map((m) => (
            <label key={m.id} className="choice" data-selected={wert.mode === m.id}>
              <input type="radio" name={`ab-modus-${wert.id}`} checked={wert.mode === m.id} onChange={() => set({ mode: m.id })} />
              <span>
                <b>{m.name}<span className={`dsgvo dsgvo-${m.klasse}`}>{m.marke}</span></b>
                <span className="hint">{m.note}</span>
              </span>
            </label>
          ))}
        </div>
      </div>

      {wert.mode === "antwortend" && (
        <>
          <div className="field">
            <label htmlFor="ab-betreff">Betreff der Antwort</label>
            <input id="ab-betreff" className="input" value={wert.subject} onChange={(e) => set({ subject: e.target.value })} />
            <p className="hint">
              <code className="mono">%betreff%</code> wird durch den Betreff der eingegangenen Mail ersetzt.
            </p>
          </div>
          <div className="field">
            <label htmlFor="ab-text">Text der Antwort</label>
            <textarea id="ab-text" className="textarea" style={{ minHeight: 170 }} value={wert.body} onChange={(e) => set({ body: e.target.value })} />
          </div>
          <div className="notice" style={{ marginBottom: 14 }}>
            <strong>Was der Agent von sich aus nicht beantwortet</strong>
            <ul>
              <li>Newsletter, Verteiler und alles mit Abmeldelink — eine Antwort ginge an alle</li>
              <li>Absender wie <code className="mono">no-reply@</code> und Zustellberichte</li>
              <li>Fremde Abwesenheitsnotizen — sonst antworten sich zwei Automaten endlos</li>
              <li>Alles mit Spam-Verdacht — eine Antwort bestätigt dem Versender deine Adresse</li>
              <li>Denselben Absender ein zweites Mal</li>
              <li>Mehr als 30 Antworten je Stunde</li>
            </ul>
          </div>
        </>
      )}

      <label className="v-check">
        <input type="checkbox" checked={wert.active} onChange={(e) => set({ active: e.target.checked })} />
        Eingeschaltet
      </label>
    </div>
  );
}
